/**
 * Centralised config — single source of truth for all environment variables.
 *
 * Environment variables used:
 *   REACT_APP_API_URL                      — Backend API base URL
 *   REACT_APP_GTM_ID                       — Google Tag Manager container ID
 *   REACT_APP_GA4_MEASUREMENT_ID           — Google Analytics 4 measurement ID
 *   REACT_APP_STAGE                        — Deployment stage (production, staging, development)
 *   REACT_APP_OBSERVABILITY_STAGE          — Observability stage (fallback for REACT_APP_STAGE)
 *   REACT_APP_FORWARD_CONSOLE_TO_BACKEND   — Enable console log forwarding ("true"/"false")
 *   REACT_APP_OBSERVABILITY_LOG_URL        — URL for forwarded logs
 *   NODE_ENV                               — CRA-injected build mode
 *
 * All consumers import { config } from '@/infra/config' instead of
 * reading process.env directly.
 */
const RAW_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/**
 * Backend origin without the `/api` suffix.
 * Useful for non-`/api` backend routes like `/auth/google` (OAuth redirect).
 */
const API_ORIGIN = RAW_API_URL.replace(/\/api\/?$/, '');

export const config = {
  /** Backend API base URL (includes `/api`). */
  apiUrl: RAW_API_URL,

  /** Backend origin (no `/api` suffix) — for non-API backend routes like `/auth/google`. */
  apiOrigin: API_ORIGIN,

  /** Google Tag Manager container ID. */
  gtmId: process.env.REACT_APP_GTM_ID || '',

  /** Google Analytics 4 measurement ID. */
  ga4Id: process.env.REACT_APP_GA4_MEASUREMENT_ID || '',

  /** Deployment stage label. REACT_APP_STAGE takes precedence; REACT_APP_OBSERVABILITY_STAGE is the legacy fallback. */
  stage: process.env.REACT_APP_STAGE || process.env.REACT_APP_OBSERVABILITY_STAGE || 'development',

  /** Raw NODE_ENV value. */
  nodeEnv: process.env.NODE_ENV || 'development',

  /** Convenience boolean — true when NODE_ENV === 'production'. */
  isProduction: process.env.NODE_ENV === 'production',

  /** Convenience boolean — true when NODE_ENV !== 'production'. */
  isDevelopment: process.env.NODE_ENV !== 'production',

  /** Whether console logs should be forwarded to the backend. */
  forwardConsoleLogs:
    String(process.env.REACT_APP_FORWARD_CONSOLE_TO_BACKEND || '').toLowerCase() === 'true',

  /** URL endpoint for forwarded console logs. */
  observabilityLogUrl: process.env.REACT_APP_OBSERVABILITY_LOG_URL || '',
} as const;
